/** 변동 — how every tracked keyword moved over a window (S5).
 *
 *  분석 compares the ranks of the chosen window (일·주·월) keyword by keyword;
 *  기록 is the change log the tracker writes at each snapshot (`?view=log`);
 *  문구 효과 compares what followed a change of name or subtitle (`?view=effects`).
 *  Replaces the old Analysis page and the Recent Changes list of the old
 *  Tracking page.
 *
 *  The server cuts the list: filters and a page of 100 go in the request
 *  (`keywordAnalysis(period, opts)`), `total` says how many matched. The
 *  summary and the per-app chips are the whole portfolio whatever the filter.
 *  Everything is in the address: period, view, app, status, search, ranked-only
 *  and page. */

import { useEffect, useMemo, useState } from 'react'
import { ChevronRight, Search, TrendingUp } from 'lucide-react'
import type { AnalysisPeriod, AppChanges, Mover, SummaryRow } from '@/api/client'
import { useOpenKeyword } from '@/lib/detail'
import { useKeywordAnalysis, useTrackedKeywordSearch } from '@/lib/queries'
import { useDebounced } from '@/lib/useDebounced'
import { cn } from '@/lib/utils'
import {
  AppIcon,
  Button,
  CountryTag,
  DataTable,
  Delta,
  Dialog,
  EmptyState,
  EntryExit,
  ErrorState,
  FilterBar,
  FilterSelect,
  Input,
  PageHeader,
  Pager,
  SearchInput,
  Segmented,
  Sparkline,
  StatusDot,
  useQueryParam,
  useSetQueryParams,
  type Column,
} from '@/ui'
import ChangeLog from './ChangeLog'
import Effects from './Effects'

export const PAGE_SIZE = 100

const PERIODS: { value: AnalysisPeriod; label: string }[] = [
  { value: 'daily', label: '일' },
  { value: 'weekly', label: '주' },
  { value: 'monthly', label: '월' },
]

const STATUS_OPTIONS = [
  { value: '', label: '상태 전체' },
  { value: 'up', label: '상승' },
  { value: 'down', label: '하락' },
  { value: 'new', label: '신규' },
  { value: 'lost', label: '이탈' },
  { value: 'same', label: '유지' },
]

const VIEWS = [
  { value: 'analysis', label: '분석' },
  { value: 'log', label: '기록' },
  { value: 'effects', label: '문구 효과' },
]

function asPeriod(v: string): AnalysisPeriod {
  return v === 'daily' || v === 'monthly' ? v : 'weekly'
}

export default function Movement() {
  const [view, setView] = useQueryParam('view', 'analysis')
  const [period, setPeriod] = useQueryParam('period', 'weekly')
  const [finder, setFinder] = useState(false)
  const log = view === 'log'
  const effects = view === 'effects'
  const analysis = !log && !effects

  return (
    <>
      <PageHeader
        title="변동"
        actions={
          <>
            {analysis && <Segmented label="기간" size="sm" value={asPeriod(period)} onChange={setPeriod} options={PERIODS} />}
            <Button icon={Search} onClick={() => setFinder(true)}>
              키워드 찾기
            </Button>
          </>
        }
      >
        <Segmented label="보기" size="sm" value={analysis ? 'analysis' : view} onChange={setView} options={VIEWS} />
      </PageHeader>
      <KeywordFinder open={finder} onClose={() => setFinder(false)} />
      {log ? <ChangeLog /> : effects ? <Effects /> : <Analysis period={asPeriod(period)} />}
    </>
  )
}

// ── 분석 ────────────────────────────────────────────────────────────────────

function Analysis({ period }: { period: AnalysisPeriod }) {
  const open = useOpenKeyword()
  const setParams = useSetQueryParams()
  const [app] = useQueryParam('app')
  const [status] = useQueryParam('status')
  const [q] = useQueryParam('q')
  const [rankedParam] = useQueryParam('ranked', '1')
  const [pageParam] = useQueryParam('page', '1')
  const ranked = rankedParam !== '0'
  const page = Math.max(1, Number.parseInt(pageParam, 10) || 1)

  // The search box keeps its own text and moves the address once typing stops.
  const [text, setText] = useState(q)
  const typed = useDebounced(text, 300)
  useEffect(() => {
    if (q !== typed) setText(q)
  }, [q]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (typed !== q) setParams({ q: typed, page: '' })
  }, [typed]) // eslint-disable-line react-hooks/exhaustive-deps

  const opts = useMemo(
    () => ({
      appId: app ? Number(app) : undefined,
      status: status ? [status as Mover['status']] : undefined,
      q: q || undefined,
      ranked,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    }),
    [app, status, q, ranked, page],
  )
  const query = useKeywordAnalysis(period, opts)
  const data = query.data

  const [openIds, setOpenIds] = useState<Set<number>>(new Set())
  const toggle = (id: number) =>
    setOpenIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const filtered = !!(app || status || q || !ranked)
  const reset = () => {
    setText('')
    setParams({ app: '', status: '', q: '', ranked: '', page: '' })
  }

  const moverDelta = (m: Mover) =>
    m.status === 'same' ? <Delta status={m.status} /> : <Delta status={m.status} delta={m.delta} before={m.past_rank} after={m.current_rank} />

  const columns: Column<Mover>[] = [
    {
      key: 'rank',
      header: '순위',
      width: '64px',
      card: 'meta',
      className: 'font-semibold tabular-nums',
      cell: (m) => (m.current_rank > 0 ? `#${m.current_rank}` : <span className="font-normal text-muted-foreground">—</span>),
    },
    {
      key: 'term',
      header: '키워드',
      card: 'title',
      cell: (m) => (
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate font-medium">{m.term}</span>
          <CountryTag code={m.country} />
        </span>
      ),
    },
    {
      key: 'app',
      header: '앱',
      card: 'hidden',
      cell: (m) => (
        <span className="inline-flex min-w-0 items-center gap-2">
          <AppIcon src={m.app_icon} name={m.app_name} size={20} />
          <span className="truncate">{m.app_name}</span>
        </span>
      ),
    },
    {
      key: 'delta',
      header: '변동',
      card: 'hidden',
      cell: (m) => moverDelta(m),
    },
    {
      // the phone's line under the keyword: which app, and how it moved
      key: 'appDelta',
      header: '앱 · 변동',
      card: 'sub',
      cardOnly: true,
      cell: (m) => (
        <span className="flex min-w-0 items-center gap-2 text-table text-foreground">
          <AppIcon src={m.app_icon} name={m.app_name} size={20} />
          <span className="min-w-0 truncate text-muted-foreground">{m.app_name}</span>
          <span className="shrink-0">{moverDelta(m)}</span>
        </span>
      ),
    },
    {
      key: 'spark',
      header: '추이',
      card: 'line',
      cell: (m) => <Sparkline series={m.series} label={`${m.term} 순위 추이`} />,
    },
    {
      key: 'top10',
      header: '상위 10',
      align: 'right',
      card: 'line',
      cardHide: (m) => m.app_changes.entered_count + m.app_changes.left_count === 0,
      cell: (m) => <EntryExit entered={m.app_changes.entered_count} exited={m.app_changes.left_count} />,
    },
    {
      key: 'expand',
      header: <span className="sr-only">펼치기</span>,
      width: '44px',
      card: 'actions',
      cell: (m) => {
        const on = openIds.has(m.keyword_id)
        return (
          <button
            type="button"
            aria-expanded={on}
            aria-label={`${m.term} 상위 10 앱 변화 ${on ? '접기' : '펼치기'}`}
            onClick={(e) => {
              e.stopPropagation()
              toggle(m.keyword_id)
            }}
            onKeyDown={(e) => e.stopPropagation()}
            className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-meta font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <ChevronRight size={16} aria-hidden className={cn('transition-transform', on && 'rotate-90')} />
            <span className="md:sr-only">상위 10 앱 변화</span>
          </button>
        )
      },
    },
  ]

  return (
    <div className="space-y-4">
      <FilterBar>
        <FilterSelect
          label="앱"
          value={app}
          onChange={(v) => setParams({ app: v, page: '' })}
          options={[{ value: '', label: '앱 전체' }, ...(data?.apps ?? []).map((a) => ({ value: String(a.app_id), label: a.app_name ?? `앱 ${a.app_id}` }))]}
        />
        <FilterSelect label="상태" value={status} onChange={(v) => setParams({ status: v, page: '' })} options={STATUS_OPTIONS} />
        <SearchInput value={text} onChange={setText} placeholder="키워드 검색" />
        <label className="inline-flex h-8 cursor-pointer select-none items-center gap-2 text-table">
          <input
            type="checkbox"
            checked={ranked}
            onChange={(e) => setParams({ ranked: e.target.checked ? '' : '0', page: '' })}
            className="h-4 w-4 accent-accent"
          />
          순위권만
        </label>
      </FilterBar>

      {query.error && !data ? (
        <ErrorState title="변동을 불러오지 못했다" error={query.error} onRetry={() => query.refetch()} />
      ) : data && data.summary.total === 0 ? (
        <EmptyState
          icon={TrendingUp}
          title="변동 데이터가 아직 없다"
          description="서로 다른 날 스냅샷이 쌓이면 이 기간의 순위 변화를 비교한다."
        />
      ) : (
        <>
          {data && <Summary summary={data.summary} apps={data.apps} app={app} onPick={(id) => setParams({ app: app === id ? '' : id, page: '' })} />}
          <Pager total={data?.total ?? 0} pageSize={PAGE_SIZE} page={page} onPage={(p) => setParams({ page: p > 1 ? String(p) : '' })} />
          <DataTable
            label="키워드 변동"
            columns={columns}
            rows={data?.movers}
            rowKey={(m) => m.keyword_id}
            loading={query.isLoading}
            error={query.error}
            onRetry={() => query.refetch()}
            onRowClick={(m) => open(m.app_id, m.keyword_id)}
            expand={{ isOpen: (m) => openIds.has(m.keyword_id), render: (m) => <AppChangesDetail changes={m.app_changes} /> }}
            empty={
              <EmptyState
                title="조건에 맞는 키워드가 없다"
                description={filtered ? '필터를 풀면 더 보인다.' : undefined}
                actions={filtered ? <Button onClick={reset}>필터 지우기</Button> : undefined}
              />
            }
          />
          {(data?.total ?? 0) > PAGE_SIZE && (
            <Pager total={data?.total ?? 0} pageSize={PAGE_SIZE} page={page} onPage={(p) => setParams({ page: p > 1 ? String(p) : '' })} />
          )}
        </>
      )}
    </div>
  )
}

function Summary({ summary, apps, app, onPick }: { summary: SummaryRow; apps: SummaryRow[]; app: string; onPick: (appId: string) => void }) {
  return (
    <section aria-label="요약" className="space-y-3 rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
        <Tally label="상승" value={summary.improved} className="text-delta-up" />
        <Tally label="하락" value={summary.declined} className="text-delta-down" />
        <Tally label="유지" value={summary.unchanged} className="text-muted-foreground" />
        <span className="ml-auto text-meta text-muted-foreground">
          추적 키워드 <span className="tabular-nums">{summary.total.toLocaleString('ko-KR')}</span>개
        </span>
      </div>
      {apps.length > 1 && (
        <ul className="flex flex-wrap gap-2 border-t border-border pt-3">
          {apps.map((a) => {
            const id = String(a.app_id)
            const on = app === id
            return (
              <li key={id}>
                <button
                  type="button"
                  aria-pressed={on}
                  title={`상승 ${a.improved} · 하락 ${a.declined} · 유지 ${a.unchanged}`}
                  onClick={() => onPick(id)}
                  className={cn(
                    'inline-flex h-7 items-center gap-2 rounded-full border px-2.5 text-meta transition-colors',
                    on ? 'border-accent bg-accent/10' : 'border-border bg-background hover:bg-muted',
                  )}
                >
                  <AppIcon src={a.app_icon} name={a.app_name ?? ''} size={16} />
                  <span className="max-w-[9rem] truncate font-medium">{a.app_name}</span>
                  <span className="tabular-nums text-delta-up">
                    <span aria-hidden>▲</span>
                    <span className="sr-only">상승 </span>
                    {a.improved}
                  </span>
                  <span className="tabular-nums text-delta-down">
                    <span aria-hidden>▼</span>
                    <span className="sr-only">하락 </span>
                    {a.declined}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

function Tally({ label, value, className }: { label: string; value: number; className?: string }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span className={cn('text-stat font-semibold tabular-nums', className)}>{value.toLocaleString('ko-KR')}</span>
      <span className="text-table text-muted-foreground">{label}</span>
    </div>
  )
}

// ── 행 펼침: 상위 10 앱 목록의 변화 ────────────────────────────────────────

function AppChangesDetail({ changes }: { changes: AppChanges }) {
  const empty = changes.entered.length === 0 && changes.left.length === 0 && changes.moved.length === 0
  if (empty) return <p className="text-meta text-muted-foreground">이 기간에 상위 10 앱 목록이 바뀌지 않았다.</p>
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <ChangeColumn tone="published" title="진입" rows={changes.entered.map((a) => ({ id: a.track_id, name: a.name, note: `#${a.rank}` }))} />
      <ChangeColumn tone="failed" title="이탈" rows={changes.left.map((a) => ({ id: a.track_id, name: a.name, note: `전 #${a.rank}` }))} />
      <ChangeColumn tone="neutral" title="이동" rows={changes.moved.map((a) => ({ id: a.track_id, name: a.name, note: `#${a.from} → #${a.to}` }))} />
    </div>
  )
}

function ChangeColumn({ tone, title, rows }: { tone: 'published' | 'failed' | 'neutral'; title: string; rows: { id: number; name: string; note: string }[] }) {
  return (
    <div className="min-w-0">
      <h3 className="mb-1.5 flex items-center gap-2 text-meta font-medium text-muted-foreground">
        <StatusDot tone={tone} />
        {title} <span className="tabular-nums">{rows.length}</span>
      </h3>
      {rows.length === 0 ? (
        <p className="text-meta text-muted-foreground">—</p>
      ) : (
        <ul className="space-y-1">
          {rows.map((r) => (
            <li key={r.id} className="flex items-baseline justify-between gap-2 text-table">
              <a
                href={`https://apps.apple.com/app/id${r.id}`}
                target="_blank"
                rel="noreferrer"
                className="min-w-0 truncate hover:text-accent hover:underline"
              >
                {r.name || `app ${r.id}`}
              </a>
              <span className="shrink-0 tabular-nums text-muted-foreground">{r.note}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ── 키워드 찾기 ─────────────────────────────────────────────────────────────

const FINDER_LIMIT = 20

/** Pick any tracked keyword by a part of its term and open it. Replaces the
 *  old competition board's keyword list, which put every tracked keyword in a
 *  <select>. */
function KeywordFinder({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [text, setText] = useState('')
  const q = useDebounced(text.trim(), 250)
  const result = useTrackedKeywordSearch(q, FINDER_LIMIT, open)
  const openKeyword = useOpenKeyword()
  const items = result.data?.items ?? []
  const total = result.data?.total ?? 0
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="키워드 찾기"
      description="추적 중인 키워드를 단어 일부로 찾아 연다."
      footer={<Button onClick={onClose}>닫기</Button>}
    >
      <div className="space-y-3">
        <Input data-autofocus aria-label="키워드" placeholder="키워드 일부" value={text} onChange={(e) => setText(e.target.value)} />
        {result.error ? (
          <ErrorState title="키워드를 불러오지 못했다" error={result.error} />
        ) : items.length === 0 ? (
          <p className="py-4 text-center text-table text-muted-foreground">{result.isLoading ? '찾는 중' : '맞는 키워드가 없다.'}</p>
        ) : (
          <>
            <ul className="max-h-72 divide-y divide-border overflow-y-auto rounded-md border border-border">
              {items.map((k) => (
                <li key={k.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onClose()
                      openKeyword(k.app_id, k.id)
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-table hover:bg-muted"
                  >
                    <span className="min-w-0 flex-1 truncate font-medium">{k.term}</span>
                    <CountryTag code={k.country} />
                    <span className="max-w-[40%] shrink-0 truncate text-meta text-muted-foreground">{k.app_name}</span>
                  </button>
                </li>
              ))}
            </ul>
            {total > items.length && (
              <p className="text-meta text-muted-foreground">
                {total.toLocaleString('ko-KR')}개 중 {items.length}개만 보인다. 단어를 더 넣으면 좁혀진다.
              </p>
            )}
          </>
        )}
      </div>
    </Dialog>
  )
}
